import { useCallback, useEffect, useMemo, useState } from "react";
import { FieldValues, useFormContext, Validate } from "react-hook-form";
import { useAppSelector } from "../../../app/hooks";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { selectCustomerUserById } from "../customerUsersSlice";
import { CustomerUser, WorkLocation } from "../types/queries";
import { WorkLocationOption } from "../types/types";
import AdditionalFields from "./AdditionalFields";
import CustomerUserPrefill from "./CustomerUserPrefill";

type Props = {
  customerUsers?: CustomerUser[];
  userId?: string;
  userData?: CustomerUser;
  isCustomerUser?: boolean;
  validateEmailAddress: Validate<string, FieldValues>;
  disablePrefill?: boolean;
  isPolling?: boolean;
  workLocations?: WorkLocation[];
};

export const formatWorkLocations = (
  workLocations: WorkLocation[],
): WorkLocationOption[] =>
  workLocations.map(({ id, name, active }) => ({
    id,
    name,
    active,
  }));

const UserForm = ({
  userId,
  userData,
  customerUsers,
  isCustomerUser,
  validateEmailAddress,
  disablePrefill,
  isPolling,
  workLocations,
}: Props) => {
  const [prefilledUser, setPrefilledUser] = useState<CustomerUser | null>(null);
  const [createNewUser, setCreateNewUser] = useState(false);

  const {
    reset,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const { status } = useAppSelector((state) => state.customerUsers);
  const isLoading = status === "pending" || !!isPolling;

  const userFromReduxState = useAppSelector((state) =>
    selectCustomerUserById(state, userId ?? ""),
  );

  // Use user data from userData prop if available
  // Otherwise use user data from redux state
  const selectedUser = useMemo(
    () => userData ?? userFromReduxState,
    [userData, userFromReduxState],
  );

  const resetToEmpty = useCallback(
    () =>
      reset({
        id: "",
        firstName: "",
        lastName: "",
        emailAddress: "",
        title: "",
        phone: "",
        customerUserWorkLocationIds: [],
      }),
    [reset],
  );

  useEffect(() => {
    if (!prefilledUser) {
      resetToEmpty();
    }
  }, [prefilledUser, resetToEmpty]);

  const getSelectedUserWorkLocations = useCallback(
    (user?: CustomerUser) =>
      workLocations?.filter(
        (workLocation) =>
          user?.account?.customerUserWorkLocationIds &&
          user?.account?.customerUserWorkLocationIds.includes(workLocation.id),
      ) || [],
    [workLocations],
  );

  const selectedUserWorkLocations = useMemo(
    () => getSelectedUserWorkLocations(selectedUser),
    [selectedUser, getSelectedUserWorkLocations],
  );

  // cleanup
  useEffect(() => () => resetToEmpty(), [resetToEmpty]);

  useEffect(() => {
    if (selectedUser) {
      // edit existing user
      const formattedValues = {
        ...selectedUser.account,
        id: selectedUser.id,
        customerUserWorkLocationIds: formatWorkLocations(
          selectedUserWorkLocations,
        ),
      };

      // set default values from selected user
      reset(formattedValues);
    }
  }, [selectedUser, reset, selectedUserWorkLocations, userId]);

  const handleUserPrefill = (customerUser: CustomerUser | null) => {
    if (!customerUser) return setPrefilledUser(null);

    setPrefilledUser(customerUser);

    const prefilledCustomerUserWorkLocations =
      getSelectedUserWorkLocations(customerUser);

    const {
      account: { customerUserInviteStatus, customerUserCompanyId, ...rest },
    } = customerUser;

    const prefilledUser = {
      ...rest,
      id: customerUser.id,
      customerUserWorkLocationIds: formatWorkLocations(
        prefilledCustomerUserWorkLocations,
      ),
    };

    Object.entries(prefilledUser).forEach(([key, value]) => {
      setValue(key, value, {
        shouldDirty: true,
      });
    });
  };

  const clearEmailError = () => {
    if (errors.emailAddress) {
      clearErrors("emailAddress");
    }
  };

  const showAdditionalFields =
    disablePrefill || createNewUser || !!prefilledUser || userId;

  return (
    <>
      <OverlaySpinner isLoading={isLoading} />
      {!showAdditionalFields ? (
        <CustomerUserPrefill
          customerUsers={customerUsers}
          handleUserPrefill={handleUserPrefill}
          clearEmailError={clearEmailError}
          validateEmailAddress={validateEmailAddress}
          prefilledUser={prefilledUser}
          setCreateNewUser={setCreateNewUser}
          error={!!errors.emailAddress}
          isCustomerUser={isCustomerUser}
        />
      ) : (
        <AdditionalFields
          prefilledUser={prefilledUser}
          isCustomerUser={isCustomerUser}
          userId={userId}
          clearEmailError={clearEmailError}
          validateEmailAddress={validateEmailAddress}
          noBorder={!!isCustomerUser}
        />
      )}
    </>
  );
};

export default UserForm;
